import { useState } from 'react';
import posthog from 'posthog-js';
import { useSelector } from 'react-redux';
import useMountEffect from '../../hooks/useMountEffect';
import { isInternalTech } from '../../utils/AuthUtils';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { AGENT_CHAT_FEATURE_FLAG, OSCAR_CONTEXT_FEATURE_FLAG } from '../../utils/EnvConfigUtils';

const readFlag = (flag: string): boolean => !!posthog.isFeatureEnabled(flag);

/** A PostHog flag read into state and kept current; see `useAgentChatEnabled` for why. */
const useSubscribedFlag = (flag: string): boolean => {
	const [enabled, setEnabled] = useState(() => readFlag(flag));

	useMountEffect(() => {
		setEnabled(readFlag(flag));
		return posthog.onFeatureFlags(() => setEnabled(readFlag(flag)));
	});

	return enabled;
};

/**
 * Who Oscar serves: buyers, and on the supplier side a buyer's internal team. Not third-party
 * suppliers: the agent has no tools for them, and the harness refuses their login.
 *
 * An internal team is a private facility (`isInternalTech`) whose company names the buyer it
 * belongs to. The app sets the two together; `privateBuyerCompanyId` is also what the harness and
 * the tool server read, so a facility marked private without one would only meet a refused login.
 */
export const isOscarAudience = (userType: string, currentUser: unknown): boolean =>
	userType === ROLE_TYPES.BUYER ||
	(userType === ROLE_TYPES.SUPPLIER &&
		isInternalTech(currentUser) &&
		!!nullSafeGet('facility.supplierCompany.privateBuyerCompanyId', currentUser));

/**
 * Whether Oscar is on for this user — the `agent-chat` PostHog flag, the same
 * one that reveals the menu items in `EnvConfigUtils` — and only for its
 * audience (`isOscarAudience`). The flag alone isn't enough. It is evaluated
 * per person, so someone who is a buyer admin in one account would also see
 * Oscar in a third-party supplier one. Who within the audience (admins) is the
 * flag's targeting, in PostHog.
 *
 * Read into state and subscribed, not read during render. `session_thunks`
 * renders the app before `ThirdPartyService.boot` calls `posthog.identify`, so
 * the first evaluation is anonymous — and if that came back false, nothing
 * re-rendered when the real flags landed. An enabled user simply never saw
 * Oscar until some unrelated update happened to refresh the component.
 */
const useAgentChatEnabled = (userType: string): boolean => {
	const enabled = useSubscribedFlag(AGENT_CHAT_FEATURE_FLAG);
	const currentUser = useSelector((state) => nullSafeGet('session.currentUser', state));
	return enabled && isOscarAudience(userType, currentUser);
};

/**
 * Whether Oscar's page context is on: `agent-chat` (for Oscar's audience) plus the `oscar-context` flag.
 * Off, no record is sent with messages, the dock has no context bar and the Ask Oscar inputs on
 * record pages are hidden.
 */
export const useOscarContextEnabled = (userType: string): boolean => {
	const chatEnabled = useAgentChatEnabled(userType);
	const contextEnabled = useSubscribedFlag(OSCAR_CONTEXT_FEATURE_FLAG);
	return chatEnabled && contextEnabled;
};

export default useAgentChatEnabled;
